import { IconCheck } from '@tabler/icons-react'
import { isArray } from 'lodash'

import { ButtonCTA } from 'components/ui/button-cta'
import Text from 'components/ui/text'
import { NOT_TRIAL_PLANS } from 'utils/consts.mjs'
import { isFreeTrialUsed } from 'utils/freeVersionBlock'
import { translate } from 'utils/translation'
import { BenefitItem } from 'features/pricing/consts'
import { Mixpanel, MIXPANEL_PROPS } from 'utils/mixpanel'
import { useUser } from 'hooks/use-user'
import { getButtonText } from 'features/pricing/utils'

import { Price } from './price'

type PriceProps = {
  value: string
  currency: string
}

type ItemProps = {
  title: string
  description: readonly string[]
  benefits: BenefitItem[]
  isBest?: boolean
  price?: number | PriceProps
  tier: string
  buttonText: string
}

interface CardProps {
  prefix?: string
  items: readonly ItemProps[]
  interval: string
  onContinue: (value: { tier: string; interval: string }) => void
  billingCycle: string
  isLoading: boolean
}

export const Cards = ({
  prefix,
  items,
  interval,
  onContinue,
  billingCycle,
  isLoading,
}: CardProps) => {
  const { subscription } = useUser()

  const isTrial = !isFreeTrialUsed() && !NOT_TRIAL_PLANS.includes(interval)

  return (
    <>
      {items.map((itm) => {
        const {
          title,
          description,
          benefits,
          isBest,
          price,
          tier,
          buttonText,
        } = itm

        let priceValue: string
        let priceCurrency: string

        if (price === undefined) {
          priceValue = '0'
          priceCurrency = '$'
        } else if (typeof price === 'number') {
          priceValue = price.toString()
          priceCurrency = '$'
        } else {
          priceValue = price.value
          priceCurrency = price.currency
        }

        return (
          <div
            key={title}
            className="tw-relative tw-flex tw-w-full tw-min-w-[260px] tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-zinc-200 tw-p-5 tw-shadow-[0px_10px_25px_-12px_rgba(0,0,0,0.05)] lg:tw-p-8"
          >
            {isBest && (
              <Text className="tw-z-1 tw-absolute tw-left-8 tw-top-0 tw-m-0 tw--translate-y-1/2 tw-transform tw-rounded-full tw-border tw-border-dashed tw-border-orange-500 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-uppercase tw-text-orange-500">
                best_value
              </Text>
            )}
            <div className="tw-flex-grow">
              <Text className="tw-m-0 tw-p-0 tw-text-2xl tw-font-bold">
                {title}
              </Text>
              <Text className="tw-m-0 tw-p-0 tw-text-sm tw-font-normal tw-leading-6 tw-text-zinc-600 lg:tw-text-base">
                {description.join(' ')}
              </Text>
            </div>
            <div className="tw-h-[1px] tw-w-full tw-bg-zinc-200" />
            <div>
              {isTrial && (
                <Text className="tw-m-0 tw-text-sm tw-font-medium">
                  free_trial_then
                </Text>
              )}
              <Price
                billingCycle={billingCycle}
                prefix={prefix}
                isLoading={isLoading}
                priceCurrency={priceCurrency}
                priceValue={priceValue}
              />
            </div>
            <ButtonCTA
              data-testid={`card-button-${tier}-${interval}`}
              onClick={() => {
                Mixpanel.trackPlanSelectClick({
                  tier,
                  planInterval: interval,
                  checkoutModalButtonLocation:
                    MIXPANEL_PROPS.CHECKOUT_MODAL_BUTTON_LOCATION.values
                      .TOP_PLAN_CARDS,
                })
                onContinue({
                  tier,
                  interval,
                })
              }}
              className="tw-h-10 tw-w-full tw-font-bold"
              size="lg"
              disabled={
                subscription?.tier === tier &&
                subscription?.planInterval === interval
              }
            >
              {translate(
                getButtonText({
                  subscription,
                  tier,
                  interval,
                  defaultText: `${buttonText}${isTrial ? '_trial' : ''}`,
                }),
              )}
            </ButtonCTA>
            <div>
              <Text className="tw-m-0 tw-text-base tw-font-medium tw-text-zinc-800">
                features_list_heading
              </Text>
              <ul className="tw-m-0 tw-mt-2 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
                {benefits.map((benefit) => (
                  <li
                    key={JSON.stringify(benefit)}
                    className="tw-flex tw-flex-row tw-items-center tw-gap-3"
                  >
                    <IconCheck
                      width={16}
                      height={16}
                      className="tw-text-orange-500"
                    />
                    <Text
                      as="span"
                      className="tw-text-xs tw-font-normal tw-lowercase tw-text-zinc-600"
                    >
                      {isArray(benefit) ? translate(...benefit) : benefit}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      })}
    </>
  )
}
